/**
 * Portfolio Data for Raj Aryan
 * AI Agent Engineer & Architect — Agentic Systems, Harness Engineering & AI-Native SDLC
 * Extracted and expanded from Raj_CV (1).docx
 */

export const personalInfo = {
  name: "Raj Aryan",
  hindiName: "राज आर्यन",
  title: "AI Agent, Cloud & Backend Engineer",
  taglines: [
    "Building Agentic AI & Autonomous Multi-Agent Systems",
    "Enterprise Cloud & Kubernetes Microservices Architecture",
    "AI Evaluation & High-Throughput Backend Engineering",
  ],
  location: "Bangalore, India",
  experienceYears: "2.5+ Years",
  email: "rajaryan.rsr@gmail.com",
  socials: {
    linkedin: "https://www.linkedin.com/in/mrrsr/",
    github: "https://github.com/mr-rsr",
    twitter: "https://twitter.com/hlw_rsr",
  },
  summary:
    "AI Agent and Cloud Engineer building enterprise systems that scale. I've architected the complete backend infrastructure (132 APIs, 5-tier RBAC) and multi-cloud deployment for an enterprise AI SaaS platform, delivered 7 multi-agent implementations to enterprise clients, and trained 500+ hours across global engineering teams. Specialized in multi-agent orchestration, cloud architecture, and AI system reliability.",
};

export const impactMetrics = [
  {
    id: "agent-implementations",
    domain: "AI Agent",
    metric: "7",
    title: "Multi-Agent Implementations",
    description:
      "Built enterprise agent systems across Graph RAG, Agentic RAG, and domain-specific copilots using LangGraph and ADK.",
    context: "Delivered to banking and sales intelligence clients with full evaluation harnesses and monitoring via LangSmith.",
    highlight: "Multi-agent architectures with RAGAS-driven quality gates",
    color: "emerald",
  },
  {
    id: "backend-endpoints",
    domain: "Backend",
    metric: "132",
    title: "API Endpoints Architected",
    description:
      "Built complete multi-tenant SaaS backend: 5 feature domains, 24 database collections, 5-tier RBAC, and credit management.",
    context: "Node.js/TypeScript backend supporting isolated JupyterLab/Langflow workspaces with SSO and per-session token lifecycle.",
    highlight: "Complete multi-tenant SaaS product backend built from scratch",
    color: "indigo",
  },
  {
    id: "cloud-architecture",
    domain: "Cloud",
    metric: "5+",
    title: "Microservices Re-Architected",
    description:
      "Led zero-downtime migration from Azure to AWS, redesigning containerized services for ECS, ALB, VPC, and Bedrock integration.",
    context: "Compressed response times from 2-3 seconds to sub-second latency while maintaining 100% service availability.",
    highlight: "Complete cloud infrastructure transformation with zero downtime",
    color: "teal",
  },
  {
    id: "enterprise-training",
    domain: "Training",
    metric: "500+",
    title: "Enterprise Hours Taught",
    description:
      "Hours of hands-on enterprise AI, Cloud, and Backend training delivered to Fortune & Global enterprise engineering leaders.",
    context: "Clients include Sharp Electronics, Brillio, Teamlease, Guardian Life, Vee Healthtek.",
    highlight: "Empowered 10+ enterprise teams and hundreds of engineers",
    color: "amber",
  },
];

export const skillTabsData = [
  {
    id: "ai-agents",
    label: "AI & Agents",
    skills: [
      { name: "LangChain", type: "brain" },
      { name: "LangGraph", type: "graph" },
      { name: "CrewAI", type: "bot" },
      { name: "ADK", type: "bot" },
      { name: "LlamaIndex", type: "brain" },
      { name: "Graph RAG", type: "graph" },
      { name: "AWS Bedrock", type: "cloud" },
      { name: "Azure OpenAI", type: "cloud" },
      { name: "MCP Tools", type: "tool" },
    ],
  },
  {
    id: "eval-sdlc",
    label: "Eval & SDLC",
    skills: [
      { name: "RAGAS", type: "shield" },
      { name: "DeepEval", type: "shield" },
      { name: "Claude Code", type: "bot" },
      { name: "GitHub Copilot", type: "bot" },
      { name: "Spec Kit", type: "doc" },
      { name: "GitHub Actions", type: "gauge" },
      { name: "CI/CD", type: "gauge" },
      { name: "Git", type: "branch" },
    ],
  },
  {
    id: "cloud-infra",
    label: "Cloud & Infra",
    skills: [
      { name: "AWS", type: "cloud" },
      { name: "Azure", type: "cloud" },
      { name: "GCP", type: "cloud" },
      { name: "Docker", type: "server" },
      { name: "Kubernetes", type: "server" },
      { name: "Terraform", type: "server" },
      { name: "AWS SageMaker", type: "cloud" },
      { name: "Google Vertex AI", type: "cloud" },
      { name: "MLflow", type: "server" },
    ],
  },
  {
    id: "languages-dbs",
    label: "Languages & DBs",
    skills: [
      { name: "Python", type: "code" },
      { name: "Node.js", type: "code" },
      { name: "Java", type: "code" },
      { name: "Flutter", type: "code" },
      { name: "Dart", type: "code" },
      { name: "SQL", type: "database" },
      { name: "PostgreSQL", type: "database" },
      { name: "Redis", type: "database" },
      { name: "MongoDB", type: "database" },
      { name: "MySQL", type: "database" },
      { name: "Supabase", type: "database" },
    ],
  },
];

export const skillCategories = [
  {
    category: "AI & Agent Engineering",
    notionColor: "purple",
    badge: "Core Specialization",
    description:
      "Architecting stateful, deterministic, and self-correcting multi-agent orchestrations and contextual retrieval graphs.",
    skills: [
      { name: "LangChain" },
      { name: "LangGraph" },
      { name: "CrewAI" },
      { name: "ADK" },
      { name: "Google ADK" },
      { name: "AWS Strands SDK" },
      { name: "LlamaIndex" },
      { name: "Graph RAG" },
      { name: "Agentic RAG" },
      { name: "MCP Server/Tool Development" },
      { name: "Agent Memory & Centralized State" },
      { name: "Context Engineering" },
    ],
    skillList: [
      "LangChain",
      "LangGraph",
      "CrewAI",
      "ADK",
      "Google ADK",
      "AWS Strands SDK",
      "LlamaIndex",
      "Graph RAG",
      "Agentic RAG",
      "MCP Server/Tool Development",
      "Agent Memory & Centralized State",
      "Context Engineering",
    ],
  },
  {
    category: "AI Evaluation & Production Quality",
    notionColor: "green",
    badge: "Reliability & Rigor",
    description:
      "Deterministic benchmarking, regression interception, and multi-dimensional LLM-as-a-judge validation pipelines.",
    skills: [
      { name: "RAGAS" },
      { name: "DeepEval" },
      { name: "Custom CI-Gated Evaluation Harnesses" },
      { name: "Golden-Set Regression Testing (120–150 Q&A pairs/domain)" },
      { name: "Faithfulness & Groundedness" },
      { name: "Answer Relevance" },
      { name: "Context Precision & Recall" },
      { name: "p50/p95 Latency Optimization" },
      { name: "Cost-Per-Query Tracking" },
    ],
    skillList: [
      "RAGAS",
      "DeepEval",
      "Custom CI-Gated Evaluation Harnesses",
      "Golden-Set Regression Testing (120-150 Q&A pairs/domain)",
      "Faithfulness & Groundedness",
      "Answer Relevance",
      "Context Precision/Recall",
      "p50/p95 Latency Optimization",
      "Cost-Per-Query Tracking",
    ],
  },
  {
    category: "AI-Native SDLC & Governance",
    notionColor: "blue",
    badge: "Process Modernization",
    description:
      "Standardizing agentic developer workflows, living constitutional rulebooks, and spec-driven autonomous pipelines.",
    skills: [
      { name: "Spec Kit" },
      { name: "Claude Code" },
      { name: "GitHub Copilot" },
      { name: "OpenAI Codex" },
      { name: "Amazon Q" },
      { name: "OpenCode" },
      { name: "constitution.md & AGENTS.md living rulebooks" },
      { name: "Git Worktree-based Agent Isolation" },
      { name: "Multi-Agent Development Workflows" },
      { name: "GitHub Actions CI/CD gates" },
    ],
    skillList: [
      "Spec Kit",
      "Claude Code",
      "GitHub Copilot",
      "OpenAI Codex",
      "Amazon Q",
      "OpenCode",
      "constitution.md & AGENTS.md living rulebooks",
      "Git Worktree-based Agent Isolation",
      "Multi-Agent Development Workflows",
      "GitHub Actions CI/CD gates",
    ],
  },
  {
    category: "Cloud & Infrastructure",
    notionColor: "orange",
    badge: "Enterprise Scale",
    description:
      "Multi-cloud zero-downtime architecture, containerized orchestration, secure networking, and cloud-native AI endpoints.",
    skills: [
      { name: "AWS (ECS, EC2, VPC, ALB, S3, Bedrock, CloudFront)" },
      { name: "Azure (OpenAI, Container Apps, VMs, CosmosDB, Blob Storage)" },
      { name: "GCP (Google Cloud Platform)" },
      { name: "Docker" },
      { name: "Kubernetes Microservices" },
      { name: "Terraform (IaC)" },
    ],
    skillList: [
      "AWS (ECS, EC2, VPC, ALB, S3, Bedrock, CloudFront)",
      "Azure (OpenAI, Container Apps, VMs, CosmosDB, Blob Storage)",
      "GCP",
      "Docker",
      "Kubernetes Microservices",
      "Terraform",
    ],
  },
  {
    category: "Backend & Data Architecture",
    notionColor: "red",
    badge: "High-Performance Systems",
    description:
      "Robust data modeling, distributed transactional consistency, vector indexing, and asynchronous messaging layers.",
    skills: [
      { name: "Python" },
      { name: "Node.js" },
      { name: "Java" },
      { name: "JavaScript" },
      { name: "SQL" },
      { name: "Flutter" },
      { name: "PostgreSQL" },
      { name: "MongoDB" },
      { name: "MySQL" },
      { name: "OpenSearch" },
      { name: "Astra DB" },
      { name: "Redis" },
      { name: "DocumentDB" },
    ],
    skillList: [
      "Python",
      "Node.js",
      "Java",
      "JavaScript",
      "SQL",
      "Flutter",
      "PostgreSQL",
      "MongoDB",
      "MySQL",
      "OpenSearch",
      "Astra DB",
      "Redis",
      "DocumentDB",
    ],
  },
  {
    category: "MLOps & Platforms",
    notionColor: "yellow",
    badge: "Model Lifecycle",
    description:
      "Automated continuous training, model registries, telemetry tracking, and enterprise foundation model hosting.",
    skills: [
      { name: "MLflow" },
      { name: "AWS SageMaker" },
      { name: "Google Vertex AI" },
      { name: "Azure AI Services" },
      { name: "Hugging Face Hub" },
      { name: "Git & Postman" },
    ],
    skillList: [
      "MLflow",
      "AWS SageMaker",
      "Google Vertex AI",
      "Azure AI Services",
      "Hugging Face Hub",
      "Git",
      "Postman",
    ],
  },
];

export const experiences = [
  {
    id: "enterprisesi",
    company: "EnterpriseSI (formerly Kloudstac)",
    role: "AI Agent, Cloud & Backend Engineer",
    period: "Jul 2024 – Present",
    location: "Bangalore, India",
    type: "Full-time",
    summary:
      "Core engineering architect who designed and developed the end-to-end backend and cloud architecture for an enterprise multi-tenant AI learning SaaS platform, spanning 132 API endpoints, multi-cloud console access, credit management, and 7 enterprise agentic AI implementations.",
    bullets: [
      "Led end-to-end backend development of a multi-tenant AI learning SaaS platform using Node.js, TypeScript, and MongoDB, spanning 5 feature domains, 24 collections, and 132 API endpoints, with a 5-tier RBAC system, TOTP-based 2FA, backup codes, refresh token rotation, and time-limited email recovery while architecting multi-tenant JupyterLab and Langflow environments with SSO, per-session token lifecycle management, and credit-gated execution, enabling isolated AI workspaces per learner across a shared infrastructure.",
      "Designed and deployed the platform’s cloud infrastructure on Azure (VNets, NSGs, Container Apps, VMs, CosmosDB, Blob Storage, Azure OpenAI) and led zero-downtime re-architecture to AWS (ECS, EC2, VPC, ALB, S3, PostgreSQL, DocumentDB, Bedrock, CloudFront), redesigning 5+ containerized services to AWS-native paradigms and reducing service response time from 2-3 seconds to sub-second latency.",
      "Engineered a multi-cloud console access system supporting AWS and Azure with a provider-agnostic interface, using AWS STS for scoped temporary credentials and a pooled Azure AD user model with Microsoft Graph API, with automated session expiry enforcement and a background sweep job preventing credential leakage and resource retention from abandoned sessions.",
      "Designed and shipped a granular credit management system supporting 8 AI service types across 66 AWS Bedrock models and Azure API proxying, with 8 transaction types (allocation, deduction, reward, refund, adjustment, auto-increase, auto-decrease, reclaim), per-lab spend caps, real-time usage tracking, and encrypted credential storage, replacing manual billing reconciliation entirely.",
      "Architected and delivered 7 enterprise agentic AI implementations across 5+ client engagements spanning Graph RAG, Agentic RAG, multi-agent orchestration, and domain-specific copilots for customer support, knowledge management, and workflow automation, built on LangGraph and ADK with MCP and A2A protocols, centralized memory management, monitored and evaluated via LangSmith and RAGAS, with several implementations advancing toward client deployment.",
      "Delivered 500+ hours of hands-on Python, ML, LLM, and GenAI training to 10+ enterprise clients and B2C cohorts, including Fortune and industry leaders such as Sharp Electronics, Brillio, Teamlease, Guardian Life, and Vee Healthtek, covering agent development, RAG systems, and cloud GenAI on AWS and Azure.",
    ],
    highlights: [
      {
        description:
          "Led end-to-end backend development of a multi-tenant AI learning SaaS platform using Node.js, TypeScript, and MongoDB, spanning 5 feature domains, 24 collections, and 132 API endpoints, with a 5-tier RBAC system, TOTP-based 2FA, backup codes, refresh token rotation, and time-limited email recovery while architecting multi-tenant JupyterLab and Langflow environments with SSO, per-session token lifecycle management, and credit-gated execution, enabling isolated AI workspaces per learner across a shared infrastructure.",
      },
      {
        description:
          "Designed and deployed the platform’s cloud infrastructure on Azure (VNets, NSGs, Container Apps, VMs, CosmosDB, Blob Storage, Azure OpenAI) and led zero-downtime re-architecture to AWS (ECS, EC2, VPC, ALB, S3, PostgreSQL, DocumentDB, Bedrock, CloudFront), redesigning 5+ containerized services to AWS-native paradigms and reducing service response time from 2-3 seconds to sub-second latency.",
      },
      {
        description:
          "Engineered a multi-cloud console access system supporting AWS and Azure with a provider-agnostic interface, using AWS STS for scoped temporary credentials and a pooled Azure AD user model with Microsoft Graph API, with automated session expiry enforcement and a background sweep job preventing credential leakage and resource retention from abandoned sessions.",
      },
      {
        description:
          "Designed and shipped a granular credit management system supporting 8 AI service types across 66 AWS Bedrock models and Azure API proxying, with 8 transaction types (allocation, deduction, reward, refund, adjustment, auto-increase, auto-decrease, reclaim), per-lab spend caps, real-time usage tracking, and encrypted credential storage, replacing manual billing reconciliation entirely.",
      },
      {
        description:
          "Architected and delivered 7 enterprise agentic AI implementations across 5+ client engagements spanning Graph RAG, Agentic RAG, multi-agent orchestration, and domain-specific copilots for customer support, knowledge management, and workflow automation, built on LangGraph and ADK with MCP and A2A protocols, centralized memory management, monitored and evaluated via LangSmith and RAGAS, with several implementations advancing toward client deployment.",
      },
      {
        description:
          "Delivered 500+ hours of hands-on Python, ML, LLM, and GenAI training to 10+ enterprise clients and B2C cohorts, including Fortune and industry leaders such as Sharp Electronics, Brillio, Teamlease, Guardian Life, and Vee Healthtek, covering agent development, RAG systems, and cloud GenAI on AWS and Azure.",
      },
    ],
    technologies: [
      "Node.js",
      "TypeScript",
      "Python",
      "AWS",
      "Azure",
      "Kubernetes",
      "MongoDB",
      "PostgreSQL",
      "AWS Bedrock",
      "Docker",
      "LangGraph",
      "ADK",
      "Terraform",
      "RAGAS",
      "LangSmith",
    ],
  },
  {
    id: "harting",
    company: "Harting India Private Limited",
    role: "Research and Development Intern",
    period: "May 2023 – Apr 2024",
    location: "Bangalore",
    type: "Internship",
    bullets: [
      "Researched existing inventory management systems and led the development of an end-to-end Smart IoT-based Inventory Management Application using Flutter, Dart, MySQL, Node.js, Express.js, and Sequelize, optimising inventory accuracy by 25% through seamless hardware integration and real-time data synchronisation.",
      "Built a Cognitive Connector Classifier app with Flutter and self-hosted Supabase, supporting image upload/editing and integrating an image classification API to match Harting connectors, automating connector selection and significantly improving sales team efficiency.",
      "Created a cross-platform business planning tool integrating the OpenAI API to guide users through key questions and automate generation of Business Canvas Models, identities, SWOT analyses, and pitch formats—significantly accelerating business planning workflows.",
    ],
    highlights: [
      {
        description:
          "Researched existing inventory management systems and led the development of an end-to-end Smart IoT-based Inventory Management Application using Flutter, Dart, MySQL, Node.js, Express.js, and Sequelize, optimising inventory accuracy by 25% through seamless hardware integration and real-time data synchronisation.",
      },
      {
        description:
          "Built a Cognitive Connector Classifier app with Flutter and self-hosted Supabase, supporting image upload/editing and integrating an image classification API to match Harting connectors, automating connector selection and significantly improving sales team efficiency.",
      },
      {
        description:
          "Created a cross-platform business planning tool integrating the OpenAI API to guide users through key questions and automate generation of Business Canvas Models, identities, SWOT analyses, and pitch formats—significantly accelerating business planning workflows.",
      },
    ],
    technologies: [
      "Flutter",
      "Dart",
      "MySQL",
      "Node.js",
      "Express.js",
      "Sequelize",
      "Supabase",
      "OpenAI API",
    ],
  },
];

export const projects = [
  {
    id: "hirematx-interview",
    title: "HireMatx Interview AI",
    repoUrl: "https://github.com/mr-rsr/HireMatxInterview",
    badge: "B2B Agentic SaaS",
    category: "Agentic AI & Voice Pipelines",
    period: "2024",
    location: "Personal Innovation",
    featured: true,
    tagline: "B2B SaaS conducting real-time adaptive voice interviews tailored to job descriptions and candidate responses.",
    description:
      "A full-stack B2B SaaS platform where recruiters create AI-powered interview projects and an agentic AI interviewer conducts dynamic voice interviews — adapting questions in real-time based on job requirements, live candidate responses, and running skill coverage.",
    architecture:
      "Next.js 16 & React 19 frontend with WebSocket media streaming (MediaRecorder), FastAPI Python backend, real-time STT/TTS voice pipelines, dynamic rubric evaluation, adaptive question generation, and PostgreSQL data persistence.",
    highlights: [
      "Real-time voice interview agent adapting difficulty and probing follow-ups based on candidate responses",
      "Live skill coverage tracking ensuring must-have and good-to-have competencies are thoroughly evaluated",
      "Running assessment engine paired with comprehensive post-interview rubric evaluation and scoring reports",
      "Full-stack architecture spanning Next.js 16, React 19, FastAPI, WebSockets, and PostgreSQL",
    ],
    techStack: [
      "TypeScript",
      "Next.js 16",
      "React 19",
      "Python",
      "FastAPI",
      "WebSockets",
      "STT / TTS",
      "PostgreSQL",
      "TailwindCSS",
    ],
    impact: "Automates end-to-end technical screening with adaptive question difficulty and live rubric scoring.",
  },
  {
    id: "medsched-ai",
    title: "MedschedAI — Clinic Scheduling AI",
    repoUrl: "https://github.com/mr-rsr/MedschedAI",
    badge: "MAS",
    category: "Multi-Agent Architecture & MCP",
    period: "2024",
    location: "Personal Innovation",
    featured: true,
    tagline: "Multi-agent appointment booking system with LangGraph, AWS Bedrock, Composio MCP, and Chroma RAG.",
    description:
      "An autonomous multi-agent healthcare appointment booking and patient coordination system built with LangGraph, AWS Bedrock, Composio MCP (Gmail + Google Calendar), Chroma RAG, and LangSmith observability.",
    architecture:
      "LangGraph supervisor agent dynamically routing across specialized sub-agents (RAG FAQ agent, Google Calendar booking agent, email confirmation agent) with Composio MCP tool integration and Chroma vector store.",
    highlights: [
      "Supervisor agent architecture dynamically routing requests across specialized clinical sub-agents",
      "Composio MCP integration automating live Google Calendar booking and Gmail confirmation dispatches",
      "Chroma vector store powering situational clinic knowledge retrieval with LangSmith observability",
      "Multi-interface support across Streamlit UI, interactive CLI, and production FastAPI endpoints",
    ],
    techStack: [
      "Python",
      "LangGraph",
      "AWS Bedrock",
      "Composio MCP",
      "Chroma RAG",
      "FastAPI",
      "Streamlit",
      "LangSmith",
    ],
    impact: "Autonomous scheduling and clinic FAQ retrieval with zero double-booking via MCP tool calling.",
  },
];

export const enterpriseTraining = {
  totalHours: "500+",
  clientsTrained: "10+ Enterprise Leaders",
  overview:
    "Delivered over 500 hours of intensive, high-impact enterprise training and executive workshops on Python, Machine Learning, Large Language Models, and Generative AI architectures. Mentored enterprise engineering teams, tech leads, and software architects across Global and Fortune companies on transitioning from experimental prototypes to resilient enterprise systems.",
  clients: [
    {
      name: "Sharp Electronics",
      focus: "Generative AI Integration & Cloud-Native Agentic Architectures",
    },
    {
      name: "Brillio",
      focus: "Enterprise RAG Systems, Vector Search & LLM Production Optimization",
    },
    {
      name: "Teamlease",
      focus: "Autonomous Agents, Process Automation & Scalable Python ML Pipelines",
    },
    {
      name: "Guardian Life",
      focus: "LLM Governance, Evaluation Harnesses & Secure Multi-Cloud GenAI",
    },
    {
      name: "Vee Healthtek",
      focus: "Healthcare Knowledge Retrieval, Domain RAG & Compliance-First AI",
    },
    {
      name: "B2C Tech Cohorts",
      focus: "Hands-on Agentic AI Development, LangGraph, CrewAI & Modern MLOps",
    },
  ],
  topicsCovered: [
    "Agentic AI Development & Multi-Agent Swarms (LangGraph, CrewAI, ADK)",
    "Advanced RAG Architectures (Graph RAG, Hybrid Search, Agentic Routing)",
    "LLM Evaluation & CI Harnesses (RAGAS, DeepEval, Faithfulness, Relevance)",
    "AI-Native SDLC (Spec Kit, Claude Code, Isolated Git Worktrees, living rulebooks)",
    "Production Cloud Deployment (AWS Bedrock, Azure OpenAI, Google Vertex AI)",
    "Low-Code Generative AI Tooling & Enterprise Workflow Integration",
    "MLOps Best Practices (MLflow, SageMaker, Model Monitoring, 99.9% Uptime)",
  ],
};

export const education = [
  {
    institution: "OP Jindal University",
    degree: "Bachelor of Technology (B.Tech)",
    field: "Computer Science and Engineering",
    score: "CGPA: 9.1 / 10.0",
    location: "Raigarh, India",
    honors: "Graduated with High Distinction (Top Tier Academic Honors)",
    coursework: [
      "Machine Learning",
      "Operating Systems",
      "Data Structures and Algorithms",
      "Computer Networking",
      "Database Management Systems (DBMS)",
      "Distributed Computing",
    ],
    description:
      "Distinguished academic tenure with rigorous focus on computer systems, algorithms, distributed computing, and machine learning foundations. Consistently achieved top-tier academic standing with a 9.1 CGPA.",
  },
  {
    institution: "OP Jindal School",
    degree: "Higher Secondary Certificate (Class XII)",
    field: "Science & Computer Science",
    score: "89%",
    location: "Patratu, India",
    honors: "Distinction in Science and Mathematics",
    coursework: [
      "Mathematics",
      "Physics",
      "Chemistry",
      "Information Practices",
      "English",
    ],
    description:
      "Completed higher secondary education with 89% aggregate, mastering advanced mathematics, physics, and programming foundations.",
  },
];

export const blogPosts = [
  {
    id: "build-cage-just-bird-guide-harness-engineering",
    title: "Build the Cage, Not Just the Bird: A Guide to Harness Engineering",
    url: "https://www.linkedin.com/pulse/build-cage-just-bird-guide-harness-engineering-raj-aryan-thxec/",
    date: "LinkedIn Pulse",
    readTime: "7 min read",
    category: "Harness Engineering & AI Reliability",
    excerpt:
      "Why deploying raw agent models without deterministic harnesses leads to silent production failures. A deep dive into golden datasets, CI-gated regression testing, and production evaluation harnesses using RAGAS and DeepEval.",
    tags: ["Harness Engineering", "AI Evaluation", "RAGAS", "DeepEval", "CI/CD"],
    highlights: [
      "Why building the agent (the bird) is only half the battle without the evaluation harness (the cage)",
      "Establishing deterministic evaluation gates and catching regressions pre-production",
      "Golden dataset curation and threshold calibration that cuts false-positive alerts",
    ],
  },
  {
    id: "agent-memory-from-basics-advanced",
    title: "Agent Memory: From Basics to Advanced",
    url: "https://www.linkedin.com/pulse/agent-memory-from-basics-advanced-raj-aryan-d0knc/",
    date: "LinkedIn Pulse",
    readTime: "8 min read",
    category: "Agentic Systems & Memory Architecture",
    excerpt:
      "A comprehensive architectural exploration of agent memory systems — moving beyond context window limitations into episodic memory, centralized Redis state stores, and graph-augmented recall for reliable multi-agent workflows.",
    tags: ["Agent Memory", "State Management", "LangGraph", "Redis", "Multi-Agent"],
    highlights: [
      "Overcoming context drift and hallucination loops across long-horizon interactions",
      "Architectural trade-offs between short-term, episodic, and semantic memory systems",
      "Centralized state protocols enabling scalable, multi-agent collaboration",
    ],
  },
];

const portfolioData = {
  personalInfo,
  impactMetrics,
  skillTabsData,
  skillCategories,
  experiences,
  projects,
  blogPosts,
  enterpriseTraining,
  education,
};

export default portfolioData;
